import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Building2, Phone, DollarSign } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/ai-receptionist-small-business.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const AIReceptionistForSmallBusiness = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.aiReceptionistSmallBusiness.metaTitle')}</title>
        <meta name="description" content={t('blog.aiReceptionistSmallBusiness.metaDescription')} />
        <meta name="keywords" content="AI receptionist for small business, virtual receptionist AI, automated phone answering small business, AI front desk, small business phone automation" />
        <link rel="canonical" href="https://call24x7.ai/blog/ai-receptionist-for-small-business" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/ai-receptionist-for-small-business" />
        <meta property="og:title" content={t('blog.aiReceptionistSmallBusiness.title')} />
        <meta property="og:description" content={t('blog.aiReceptionistSmallBusiness.metaDescription')} />
        <meta property="og:site_name" content="Call24x7.AI" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.aiReceptionistSmallBusiness.title'),
            "description": t('blog.aiReceptionistSmallBusiness.metaDescription'),
            "author": { "@type": "Organization", "name": "Call24x7.AI" },
            "publisher": { "@type": "Organization", "name": "Call24x7.AI", "url": "https://call24x7.ai" },
            "datePublished": "2025-11-05",
            "dateModified": "2025-11-05"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.aiReceptionistSmallBusiness.title')}
        category={t('blog.categories.smallBusiness')}
        date="November 5, 2025"
        readTime={`6 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI receptionist for small business"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.aiReceptionistSmallBusiness.intro')}
        </p>

        <div className="grid md:grid-cols-3 gap-4 my-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Building2 className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">24/7</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiReceptionistSmallBusiness.stat1')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Phone className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">100%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiReceptionistSmallBusiness.stat2')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <DollarSign className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">60%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiReceptionistSmallBusiness.stat3')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiReceptionistSmallBusiness.whatIsTitle')}</h2>
        <p>{t('blog.aiReceptionistSmallBusiness.whatIsDesc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiReceptionistSmallBusiness.whyNeedTitle')}</h2>
        <ul className="space-y-3 my-4">
          {[
            t('blog.aiReceptionistSmallBusiness.reason1'),
            t('blog.aiReceptionistSmallBusiness.reason2'),
            t('blog.aiReceptionistSmallBusiness.reason3'),
            t('blog.aiReceptionistSmallBusiness.reason4'),
            t('blog.aiReceptionistSmallBusiness.reason5')
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiReceptionistSmallBusiness.featuresTitle')}</h2>
        <p>{t('blog.aiReceptionistSmallBusiness.featuresIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li><strong>{t('blog.aiReceptionistSmallBusiness.feature1Title')}:</strong> {t('blog.aiReceptionistSmallBusiness.feature1Desc')}</li>
          <li><strong>{t('blog.aiReceptionistSmallBusiness.feature2Title')}:</strong> {t('blog.aiReceptionistSmallBusiness.feature2Desc')}</li>
          <li><strong>{t('blog.aiReceptionistSmallBusiness.feature3Title')}:</strong> {t('blog.aiReceptionistSmallBusiness.feature3Desc')}</li>
          <li><strong>{t('blog.aiReceptionistSmallBusiness.feature4Title')}:</strong> {t('blog.aiReceptionistSmallBusiness.feature4Desc')}</li>
          <li><strong>{t('blog.aiReceptionistSmallBusiness.feature5Title')}:</strong> {t('blog.aiReceptionistSmallBusiness.feature5Desc')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiReceptionistSmallBusiness.implementationTitle')}</h2>
        <ol className="list-decimal pl-6 my-4 space-y-3">
          <li><strong>{t('blog.aiReceptionistSmallBusiness.step1Title')}:</strong> {t('blog.aiReceptionistSmallBusiness.step1Desc')}</li>
          <li><strong>{t('blog.aiReceptionistSmallBusiness.step2Title')}:</strong> {t('blog.aiReceptionistSmallBusiness.step2Desc')}</li>
          <li><strong>{t('blog.aiReceptionistSmallBusiness.step3Title')}:</strong> {t('blog.aiReceptionistSmallBusiness.step3Desc')}</li>
        </ol>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.aiReceptionistSmallBusiness.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.aiReceptionistSmallBusiness.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.aiReceptionistSmallBusiness.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default AIReceptionistForSmallBusiness;
